import { useSyncExternalStore } from 'react';
import dayjs from 'dayjs';

/**
 * Nghiệp vụ chăm sóc sau dịch vụ mà API /appointments/completed CHƯA có — lưu tạm ở localStorage
 * theo id lịch hẹn. Khi backend bổ sung trường thì thay `careApi` bằng lời gọi API, giữ nguyên các hàm thuần.
 *  - Cảnh báo / biến chứng (mở → xử lý xong)
 *  - Hẹn chăm sóc lại (ngày + nội dung)
 *  - Kết quả các lần liên hệ (nghe máy / không nghe / thuê bao / hẹn gọi lại)
 *  - Khách có giới thiệu bạn bè không, thời điểm gửi link khảo sát
 */

const KEY = 'khothi.carelist.v1';

/** Gọi hỏi thăm sau dịch vụ 1 ngày; quá 3 ngày chưa chăm sóc là trễ hạn */
export const CARE_DUE_DAYS = 1;
export const CARE_SLA_DAYS = 3;
/** Liên hệ không được từng ấy lần thì xếp "Không liên lạc được" */
export const UNREACHABLE_LIMIT = 3;
/** CSAT trung bình dưới mức này coi như khách không hài lòng → cảnh báo */
export const LOW_CSAT = 3;
/** Gửi link đánh giá rồi mà sau từng ấy ngày khách chưa đánh giá thì nhắc gửi lại */
export const SURVEY_REMIND_DAYS = 2;
/** Gửi tối đa từng ấy lần, quá thì nhân viên gọi khảo sát trực tiếp thay vì làm phiền khách */
export const SURVEY_MAX_SENDS = 3;

export type SurveyChannel = 'zalo' | 'sms' | 'qr' | 'other';
export const SURVEY_CHANNEL: Record<SurveyChannel, string> = {
    zalo: 'Zalo',
    sms: 'Tin nhắn SMS',
    qr: 'Khách quét QR tại quầy',
    other: 'Kênh khác',
};
export interface SurveySend { at: number; by: string; channel: SurveyChannel }

export type ContactResult = 'answered' | 'no_answer' | 'busy' | 'callback';
export const CONTACT_RESULT: Record<ContactResult, { label: string; reached: boolean }> = {
    answered: { label: 'Khách nghe máy', reached: true },
    callback: { label: 'Khách hẹn gọi lại', reached: true },
    no_answer: { label: 'Không nghe máy', reached: false },
    busy: { label: 'Thuê bao / máy bận', reached: false },
};

export type FlagKind = 'complication' | 'complaint' | 'other';
export const FLAG_KIND: Record<FlagKind, string> = {
    complication: 'Biến chứng / phản ứng sau dịch vụ',
    complaint: 'Khách phàn nàn',
    other: 'Khác',
};

export interface CareFlag {
    kind: FlagKind;
    note: string;
    by: string;
    at: number;
    resolved?: { note: string; by: string; at: number } | null;
}
export interface ContactAttempt { at: number; by: string; result: ContactResult; note?: string; channel: 'call' | 'zalo' | 'messenger' }
export interface FollowUp { at: number; note: string; by: string; setAt: number }

export interface CareExtra {
    flags: CareFlag[];
    attempts: ContactAttempt[];
    followUp?: FollowUp | null;
    referral?: boolean | null;
    /** Các lần gửi link khảo sát cho khách tự đánh giá (mới nhất trước) */
    surveySends?: SurveySend[];
}
interface State { v: 1; byAppointment: Record<string, CareExtra> }

export const EMPTY_EXTRA: CareExtra = { flags: [], attempts: [] };

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------
const load = (): State => {
    try {
        const s = JSON.parse(localStorage.getItem(KEY) || 'null');
        if (s && s.v === 1 && s.byAppointment) return s;
    } catch { /* dữ liệu hỏng → bắt đầu lại */ }
    return { v: 1, byAppointment: {} };
};

let state: State = load();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

function set(next: State) {
    try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* đầy bộ nhớ: vẫn giữ trong phiên */ }
    state = next;
    emit();
}

const patch = (id: number | string, fn: (e: CareExtra) => CareExtra) => {
    const key = String(id);
    const cur = state.byAppointment[key] || EMPTY_EXTRA;
    set({ ...state, byAppointment: { ...state.byAppointment, [key]: fn(cur) } });
};

const subscribe = (l: () => void) => {
    listeners.add(l);
    const onStorage = (e: StorageEvent) => { if (e.key === KEY) { state = load(); emit(); } };
    window.addEventListener('storage', onStorage);
    return () => { listeners.delete(l); window.removeEventListener('storage', onStorage); };
};

export const useCareExtras = () => useSyncExternalStore(subscribe, () => state).byAppointment;
export const extraOf = (all: Record<string, CareExtra>, id: number | string): CareExtra => all[String(id)] || EMPTY_EXTRA;
/** Chỉ dùng cho test */
export const resetCareStore = () => set({ v: 1, byAppointment: {} });

export const careApi = {
    addAttempt(id: number, a: Omit<ContactAttempt, 'at'>, at = Date.now()) {
        patch(id, e => ({ ...e, attempts: [{ ...a, at }, ...e.attempts] }));
    },
    setFollowUp(id: number, at: number, note: string, by: string) {
        patch(id, e => ({ ...e, followUp: { at, note, by, setAt: Date.now() } }));
    },
    clearFollowUp(id: number) {
        patch(id, e => ({ ...e, followUp: null }));
    },
    addFlag(id: number, kind: FlagKind, note: string, by: string) {
        patch(id, e => ({ ...e, flags: [{ kind, note, by, at: Date.now(), resolved: null }, ...e.flags] }));
    },
    resolveFlag(id: number, flagAt: number, note: string, by: string) {
        patch(id, e => ({ ...e, flags: e.flags.map(f => (f.at === flagAt ? { ...f, resolved: { note, by, at: Date.now() } } : f)) }));
    },
    setReferral(id: number, v: boolean | null) {
        patch(id, e => ({ ...e, referral: v }));
    },
    markSurveySent(id: number, channel: SurveyChannel, by: string, at = Date.now()) {
        patch(id, e => ({ ...e, surveySends: [{ at, by, channel }, ...(e.surveySends || [])] }));
    },
};

// ---------------------------------------------------------------------------
// Hàm thuần
// ---------------------------------------------------------------------------
export type CareBucket = 'cared' | 'awaiting' | 'unreachable' | 'overdue' | 'due' | 'upcoming';
export const BUCKET_META: Record<CareBucket, { label: string; color: string }> = {
    awaiting: { label: 'Chờ khách đánh giá', color: 'purple' },
    overdue: { label: 'Trễ hạn chăm sóc', color: 'red' },
    due: { label: 'Đến hạn chăm sóc', color: 'orange' },
    upcoming: { label: 'Chưa đến hạn', color: 'blue' },
    unreachable: { label: 'Không liên lạc được', color: 'default' },
    cared: { label: 'Đã chăm sóc', color: 'green' },
};

export interface CareRowInput { is_cared: boolean; scheduled_at: string | null; csat: number | null }

export interface CareState {
    bucket: CareBucket;
    /** Hạn chăm sóc (ngày làm + CARE_DUE_DAYS, hoặc ngày hẹn chăm sóc lại nếu có) */
    dueAt: number | null;
    /** Số ngày trễ so với hạn SLA (>0 là trễ) */
    lateDays: number;
    openFlags: CareFlag[];
    lowCsat: boolean;
    /** Có việc cần xử lý gấp: cảnh báo đang mở hoặc CSAT thấp */
    warning: boolean;
    failedAttempts: number;
    /** Hẹn chăm sóc lại đã tới ngày (kể cả khách đã chăm sóc lần đầu) */
    followUpDue: boolean;
    /** Đã gửi link khảo sát, khách chưa đánh giá */
    surveyPending: boolean;
    /** Gửi đã lâu mà khách chưa đánh giá → nhắc lại (hoặc gọi khảo sát nếu đã gửi đủ số lần) */
    surveyRemind: boolean;
}

export const surveySendsOf = (e: CareExtra) => e.surveySends || [];

/** Lời mời đánh giá gửi khách qua Zalo / SMS */
export const surveyInviteText = (name: string, service: string, url: string) =>
    `Chào ${name ? `anh/chị ${name}` : 'anh/chị'}, cảm ơn anh/chị đã sử dụng dịch vụ${service && service !== '—' ? ` ${service}` : ''} tại Khơ Thị. `
    + `Anh/chị vui lòng dành 1 phút đánh giá chất lượng dịch vụ giúp chúng em qua link: ${url} . Ý kiến của anh/chị giúp chúng em phục vụ tốt hơn. Trân trọng!`;

/** Số lần liên hệ không được liên tiếp tính từ lần gần nhất */
export const failedStreak = (attempts: ContactAttempt[]) => {
    let n = 0;
    for (const a of attempts) { if (CONTACT_RESULT[a.result].reached) break; n++; }
    return n;
};

export function careStateOf(row: CareRowInput, extra: CareExtra, now = Date.now()): CareState {
    const doneAt = row.scheduled_at ? dayjs(row.scheduled_at) : null;
    const openFlags = extra.flags.filter(f => !f.resolved);
    const lowCsat = row.csat !== null && row.csat < LOW_CSAT;
    const failedAttempts = failedStreak(extra.attempts);
    const followUpDue = !!extra.followUp && dayjs(extra.followUp.at).startOf('day').valueOf() <= now;
    const today = dayjs(now).startOf('day');
    const lastSend = surveySendsOf(extra)[0];
    const surveyPending = !row.is_cared && !!lastSend;
    const surveyRemind = surveyPending && now - lastSend.at >= SURVEY_REMIND_DAYS * 86400000;

    let dueAt: number | null = doneAt ? doneAt.add(CARE_DUE_DAYS, 'day').startOf('day').valueOf() : null;
    if (extra.followUp && !row.is_cared) dueAt = dayjs(extra.followUp.at).startOf('day').valueOf();
    const lateDays = doneAt && !row.is_cared ? today.diff(doneAt.startOf('day'), 'day') - CARE_SLA_DAYS : 0;

    let bucket: CareBucket;
    if (row.is_cared) bucket = 'cared';
    else if (surveyPending && !surveyRemind) bucket = 'awaiting'; // đã gửi link, đang chờ khách — chưa phải việc của nhân viên
    else if (failedAttempts >= UNREACHABLE_LIMIT) bucket = 'unreachable';
    else if (extra.followUp) bucket = followUpDue ? 'due' : 'upcoming'; // khách hẹn ngày khác thì theo ngày hẹn
    else if (lateDays > 0) bucket = 'overdue';
    else if (dueAt !== null && dueAt <= now) bucket = 'due';
    else bucket = 'upcoming';

    return { bucket, dueAt, lateDays, openFlags, lowCsat, warning: openFlags.length > 0 || lowCsat, failedAttempts, followUpDue, surveyPending, surveyRemind };
}

/** Nhóm cho thanh tab nhanh — một lịch hẹn có thể thuộc nhiều nhóm (VD vừa trễ hạn vừa có cảnh báo) */
export type QuickTab = 'all' | 'todo' | 'overdue' | 'warning' | 'followup' | 'awaiting' | 'cared';
export const matchQuickTab = (tab: QuickTab, s: CareState) => {
    switch (tab) {
        case 'all': return true;
        case 'todo': return s.bucket === 'due' || s.bucket === 'overdue';
        case 'overdue': return s.bucket === 'overdue';
        case 'warning': return s.warning;
        case 'followup': return s.followUpDue;
        case 'awaiting': return s.surveyPending;
        case 'cared': return s.bucket === 'cared';
    }
};

export type CsatFilter = 'none' | 'low' | 'mid' | 'high';
export const matchCsat = (f: CsatFilter, csat: number | null) =>
    f === 'none' ? csat === null
        : csat === null ? false
            : f === 'low' ? csat < LOW_CSAT
                : f === 'mid' ? csat >= LOW_CSAT && csat < 4
                    : csat >= 4;

/** Xuất CSV (mở được bằng Excel nhờ BOM UTF-8) */
export function toCsv(rows: (string | number | null | undefined)[][]) {
    const cell = (v: string | number | null | undefined) => {
        const s = v === null || v === undefined ? '' : String(v);
        return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    return '﻿' + rows.map(r => r.map(cell).join(',')).join('\n');
}
